import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  uploadJsonToIPFS
} from '../utils/ipfsClient';
import CryptoJS from 'crypto-js';

const EnhancedFileUpload = ({ onUploadComplete, enableEncryption = false }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState('');
  const [useEncryption, setUseEncryption] = useState(false);

  // File validation
  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/', 'application/pdf', 'text/', 'application/msword',
      'application/vnd.openxmlformats-officedocument', 'video/', 'audio/'
    ];

    if (file.size > maxSize) {
      return { isValid: false, message: 'File size must be less than 10MB' };
    }

    const isAllowed = allowedTypes.some(type => file.type.startsWith(type));
    if (!isAllowed) {
      return { isValid: false, message: 'File type not supported' };
    }

    return { isValid: true };
  };

  // Get file metadata
  const getFileMetadata = (file) => ({
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type,
    lastModified: new Date(file.lastModified).toISOString()
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback((acceptedFiles) => {
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    setFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (useEncryption && !encryptionKey) {
      setError('Please provide an encryption key');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      
      // Progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 5;
          if (newProgress >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return newProgress;
        });
      }, 200);

      let uploadData;
      
      if (useEncryption && encryptionKey) {
        // Read file as array buffer
        const arrayBuffer = await file.arrayBuffer();
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
        
        // Encrypt file content
        const encrypted = CryptoJS.AES.encrypt(wordArray, encryptionKey).toString();
        
        // Create metadata for encrypted file
        const metadata = {
          originalFilename: file.name,
          mimeType: file.type,
          size: file.size,
          encrypted: true,
          timestamp: new Date().toISOString()
        };

        // Create combined data with metadata and encrypted content
        const encryptedData = {
          metadata,
          encryptedContent: encrypted
        };

        uploadData = await uploadJsonToIPFS(encryptedData);
      } else {
        // For non-encrypted files, convert to base64 for JSON storage
        const arrayBuffer = await file.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
        
        const fileData = {
          metadata: getFileMetadata(file),
          content: base64,
          encrypted: false,
          timestamp: new Date().toISOString()
        };

        uploadData = await uploadJsonToIPFS(fileData);
      }
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setSuccess('File uploaded successfully to IPFS!');
      
      if (onUploadComplete) {
        onUploadComplete({
          ...uploadData,
          metadata: getFileMetadata(file),
          encrypted: useEncryption,
          encryptionKey: useEncryption ? encryptionKey : null
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    setEncryptionKey('');
    setUseEncryption(false);
  };

  return (
    <div className="enhanced-file-upload bg-white p-6 rounded-lg shadow-md border border-gray-200">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-blue-400 bg-blue-50' 
            : file 
              ? 'border-green-400 bg-green-50' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center">
          <svg 
            className={`w-12 h-12 mb-4 ${isDragActive ? 'text-blue-500' : file ? 'text-green-500' : 'text-gray-400'}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          
          <h3 className="text-lg font-semibold mb-2">
            {file ? 'File ready for upload' : 'Drag & drop a file here, or click to select'}
          </h3>
          
          {file ? (
            <p className="text-sm text-gray-600">
              Selected: <strong>{file.name}</strong> ({formatFileSize(file.size)})
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              Supports: Documents, Images, Videos, Audio files (Max 10MB)
            </p>
          )}
        </div>
      </div>

      {enableEncryption && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="enable-encryption"
              checked={useEncryption}
              onChange={(e) => setUseEncryption(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="enable-encryption" className="text-sm font-medium">
              Enable client-side encryption
            </label>
          </div>
          
          {useEncryption && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Encryption Key:
              </label>
              <input
                type="password"
                value={encryptionKey}
                onChange={(e) => setEncryptionKey(e.target.value)}
                placeholder="Enter a secure encryption key"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Remember this key - it's required to decrypt the file later
              </p>
            </div>
          )}
        </div>
      )}

      {file && (
        <div className="mt-4">
          {uploading && (
            <div className="mb-4">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 text-center mt-1">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          <div className="flex space-x-3 justify-center">
            <button
              onClick={handleClearFile}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Clear
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                'Upload to IPFS'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedFileUpload;